import { Capacitor } from "@capacitor/core";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useState,
} from "react";

interface MobileAuthContextType {
	isMobileAuthReady: boolean;
	setMobileAuthReady: (ready: boolean) => void;
	// App resume event counter - increments each time app resumes from background
	// Used to reset SessionGuard validation and trigger re-authentication checks
	appResumeCount: number;
	triggerAppResume: () => void;
}

const MobileAuthContext = createContext<MobileAuthContextType>({
	isMobileAuthReady: !Capacitor.isNativePlatform(), // Web is always ready
	setMobileAuthReady: () => {},
	appResumeCount: 0,
	triggerAppResume: () => {},
});

export const useMobileAuth = () => useContext(MobileAuthContext);

export function MobileAuthProvider({ children }: { children: ReactNode }) {
	// On web, start as ready immediately. On mobile, start as not ready.
	const [isMobileAuthReady, setMobileAuthReady] = useState(
		!Capacitor.isNativePlatform(),
	);

	// App resume counter - used to signal SessionGuard to reset validation
	// This helps prevent stale auth state after app resumes from background
	const [appResumeCount, setAppResumeCount] = useState(0);

	// Function to trigger app resume event
	const triggerAppResume = useCallback(() => {
		setAppResumeCount((c) => c + 1);
	}, []);

	return (
		<MobileAuthContext.Provider
			value={{
				isMobileAuthReady,
				setMobileAuthReady,
				appResumeCount,
				triggerAppResume,
			}}
		>
			{children}
		</MobileAuthContext.Provider>
	);
}
